import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getCriticalityLabels } from 'src/module/authentication/selector';
import { getSelectedUnit, getUnitNames } from 'src/module/filter/selector';
import { getTriageSummaryByUnit } from 'src/module/alarm/selector';
import { setSelectedUnit } from 'src/module/filter/action';
import { toUnassignedCounts, toUnitAlarms, type UnitAlarmCount, type UnitAlarms } from './alarmVolume';

const NO_AREA = '';

export interface AlarmVolumeData {
  tiles: UnitAlarms[];
  /** Alarms on devices with no unit, by severity slot; */
  unassigned: UnitAlarmCount[];
  selectedUnit: string;
  onSelectUnit: (unit: string) => void;
}

/** Reads the per-area alarm counts (fetched by DashboardScreen) and wires tile picks to the area filter. */
export default function useAlarmVolume (): AlarmVolumeData {
  const dispatch = useDispatch();

  const criticalityLabels = useSelector(getCriticalityLabels);
  const summaryByUnit = useSelector(getTriageSummaryByUnit);
  const units = useSelector(getUnitNames);
  const selectedUnit = useSelector(getSelectedUnit);

  const tiles = useMemo(
    () => toUnitAlarms(units, summaryByUnit, criticalityLabels),
    [units, summaryByUnit, criticalityLabels]
  );
  const unassigned = useMemo(
    () => toUnassignedCounts(units, summaryByUnit, criticalityLabels),
    [units, summaryByUnit, criticalityLabels]
  );

  // Re-picking the selected area clears back to All Areas.
  const onSelectUnit = useCallback(
    (unit: string) => dispatch(setSelectedUnit(unit === selectedUnit ? NO_AREA : unit)),
    [dispatch, selectedUnit]
  );

  return { tiles, unassigned, selectedUnit, onSelectUnit };
}
